import { useContext, useState } from "react";
import { Switch, StatusIndicator } from "~/components/UI";
import ConfirmDialog from "~/components/BaseConfirmDialog";
import Table, { TableProps } from "~/components/UI/Table/Table";
import POSDetails from "./POSDetails";
import POSOptions from "./POSOptions";
import POSesContext from "./posesContext";
import IncidentsIcon from "~/components/Incidents/IncidentsIcon";
import { useTranslation } from "react-i18next";
import { i18nTableColumn } from "~/packages/i18n";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

const Status = ({ connected }: { connected: boolean }) => {
  const { t } = useTranslation();
  return (
    <StatusIndicator
      status={connected ? "active" : "disabled"}
      label={connected ? t("text.connected") : t("text.disconnected")}
    />
  );
};

const defaultColumns = [
  {
    key: "type",
    sort: true,
    i18nKey: "type",
  },
  {
    key: "posId",
    sort: true,
    i18nKey: "posId",
  },
  {
    key: "siteId",
    sort: true,
    i18nKey: "siteId",
  },
  {
    key: "connected",
    sort: true,
    i18nKey: "posStatus",
    render: (connected: boolean) => <Status connected={connected} />,
  },
  {
    key: "enabled",
    sort: true,
    i18nKey: "enabled",
    render: (enabled: boolean) => <Switch checked={enabled} />,
  },
  {
    key: "siteName",
    sort: true,
    i18nKey: "siteName",
  },
  {
    key: "siteAddress",
    sort: true,
    i18nKey: "address",
  },
  {
    key: "incidents",
    cellOptions: {
      align: "center",
      padding: "none",
      width: 50,
    },
    render: (incidents: IncidentsApi.IncidentsCount) => (
      <IncidentsIcon incidents={incidents} />
    ),
  },
  {
    key: "options",
    cellOptions: {
      align: "right",
      padding: "none",
      width: 70,
    },
  },
];

/**
 * @memberof POSes
 * @component
 * @desc POS options. Using a POSesContext.
 * @property {Api.PosDto[]} dataSource - POSes data
 * @requires POSesContext
 */

const POSTable = (props: Omit<TableProps<Api.PosDto>, "columns">) => {
  const { onUpdate, onSelect, onSort } = useContext(POSesContext);
  const { t } = useTranslation();
  const { hasAll } = usePermissions();
  const [rowSelection] = useState(
    hasAll(PERMISSION.DELETE_POS)
      ? {
          hideSelectAll: false,
          onChange: onSelect,
        }
      : undefined
  );

  const columns = defaultColumns.map((c) => {
    const column = i18nTableColumn(c, t);
    switch (column.key) {
      case "enabled":
        return {
          ...column,
          render: (enabled: boolean, pos: Api.PosDto) => (
            <ConfirmDialog
              placement="top"
              disabled={!hasAll(PERMISSION.EDIT_POS)}
              onConfirm={() =>
                onUpdate && onUpdate(pos.posId, { enabled: !enabled })
              }
            >
              <Switch
                checked={enabled}
                disabled={!hasAll(PERMISSION.EDIT_POS)}
              />
            </ConfirmDialog>
          ),
        };
      case "options":
        return {
          ...column,
          render: (_value: unknown, row: Api.PosDto) => (
            <POSOptions pos={row} />
          ),
        };
      default:
        return column;
    }
  });

  return (
    <Table
      {...props}
      columns={columns}
      onRequestSort={onSort}
      rowSelection={rowSelection}
      rowKey="posId"
      stickyHeader
      expandable={{
        highlightRow: true,
        expandedRowRender: (pos: Api.PosDto) => <POSDetails pos={pos} />,
      }}
    />
  );
};

export default POSTable;
